// src/app.tsx
import { RouterProvider, createBrowserRouter } from "react-router-dom";
import Index from "./lib/pages/Index";
import About from "./lib/pages/About";
import Business from "./lib/pages/Business";
import SignUp from "./lib/pages/SignUp";
import ForgotPassword from "./lib/pages/ForgotPassword";
import Login from "./lib/pages/Login";
import ChangePassword from "./lib/pages/ChangePassword";
import MVPLanding from "./lib/pages/MVPLanding";
import ContactUs from "./lib/pages/ContactUs";
import Legal from "./lib/pages/Legal";
import PrivacyContent from "./lib/pages/PrivacyContent";
import TermsContent from "./lib/pages/TermsContent";
import LegalContent from "./lib/pages/LegalContent";
import { QueryClient, QueryClientProvider } from "react-query";
import FAQ from "./lib/pages/FAQ";
import Blog from "./lib/pages/Blog";
import BlogDetails from "./lib/pages/BlogDetails";
import Discover from "./lib/pages/Discover";
import PersonalHome from "./lib/pages/Home";
import OfferLetter from "./lib/components/OfferLetter";
import ValidateOTP from "./lib/pages/ValidateOTP";
import PageNotFound from "./lib/pages/PageNotFound";
import AuthContext from "./lib/context/AuthContext";
import MVPLandingUser from "./lib/pages/MvplandingUser";
import GetStarted from "./lib/pages/signup/GetStarted";
import TripBookingWebView from "./lib/components/TripBookingWebView";
import TripsContextProvider from "./lib/context/TripsContext";
import Help from "./lib/pages/Help";
import Careers from "./lib/pages/Career";
import Cookies from "./lib/pages/Cookies";
import { Toaster } from "sonner";
import Verify from "./lib/pages/Verify";
import ResetPassword from "./lib/pages/ResetPassword";
import "react-phone-number-input/style.css";
import TwoFactorAuthentication from "./lib/pages/TwoFactorAuthentication";
import InviteAccept from "./lib/pages/InviteAccept";
import ResumeOnboarding from "./lib/pages/ResumeOnboarding";
import PartnerSsoContinue from "./lib/pages/PartnerSsoContinue";
import OnboardingSurvey from "./lib/pages/OnboardingSurvey";
import HrRegistration from "./lib/pages/HrRegistration";
import PlatformSurvey from "./lib/pages/PlatformSurvey";
import { captureAcquisitionFromParams } from "./lib/utils/acquisitionSource";
// import ComingSoon from "./lib/pages/ComingSoon";

// Capture on every landing route before signup/navigation can drop the tracking query.
captureAcquisitionFromParams(new URLSearchParams(window.location.search));
const router = createBrowserRouter([
  {
    path: "/landing",
    element: <MVPLanding />,
  },
  {
    path: "/",
    element: <Index />,
  },
  {
    path: "/careers",
    element: <Careers />,
  },
  {
    path: "/for-personal",
    element: <Index />,
  },
  {
    path: "/legal",
    element: <Legal />,
    children: [
      {
        path: "",
        element: <LegalContent />, // Default content for /legal
      },
      {
        path: "privacy",
        element: <PrivacyContent />,
      },
      {
        path: "terms",
        element: <TermsContent />,
      },
    ],
  },
  {
    path: "/faq",
    element: <FAQ />,
  },
  {
    path: "/blog",
    element: <Blog />,
  },
  {
    path: "/blog/:id",
    element: <BlogDetails />,
  },
  {
    path: "/contact-us",
    element: <ContactUs />,
  },
  {
    path: "/about",
    element: <About />,
  },
  {
    path: "/offer-letter",
    element: <OfferLetter />,
  },
  {
    path: "/for-business",
    element: <Business />,
  },
  {
    path: "/verify/*",
    element: <Verify />,
  },
  {
    path: "/sign-up/*",
    element: <SignUp />,
  },
  {
    path: "/login",
    element: <Login />,
  },
  {
    path: "/forgot-password",
    element: <ForgotPassword />,
  },
  {
    path: "/reset-password",
    element: <ResetPassword />,
  },
  {
    path: "/2fa",
    element: <TwoFactorAuthentication />,
  },
  {
    path: "/otp",
    element: <ValidateOTP />,
  },
  {
    path: "/invite/accept",
    element: <InviteAccept />,
  },
  {
    path: "/resume-onboarding",
    element: <ResumeOnboarding />,
  },
  {
    // Plan 008 — first-party Visaro SSO landing from the Alert SDK CTA.
    path: "/continue",
    element: <PartnerSsoContinue />,
  },
  {
    path: "/onboarding-survey",
    element: <OnboardingSurvey />,
  },
  {
    path: "/survey",
    element: <PlatformSurvey />,
  },
  {
    path: "/hr-survey",
    element: <PlatformSurvey />,
  },
  {
    path: "/hr-registration",
    element: <HrRegistration />,
  },
  {
    path: "/*",
    element: <PersonalHome openMarketplace={process.env.REACT_APP_ENABLE_MARKETPLACE === "true"} />,
  },

  {
    path: "/help",
    element: <Help />,
  },
  {
    path: "/discover",
    element: <Discover />,
  },
  {
    path: "/cookies",
    element: <Cookies />,
  },
  {
    path: "/change-password",
    element: <ChangePassword />,
  },
  {
    path: "/trip-booking",
    element: <TripBookingWebView />,
  },
  {
    path: "/userland",
    element: <MVPLandingUser />,
  },
  {
    path: "/country",
    element: <GetStarted />,
  },
  {
    path: "*",
    element: <PageNotFound />,
  },
]);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchInterval: false,
      retry: false,
      staleTime: 1000 * 60 * 5, // 5 minutes fresh
      cacheTime: 1000 * 60 * 30, // 30 minutes in cache after unused
      onError(err: unknown) {
        const pathname = typeof window !== "undefined" ? window.location.pathname : "";
        const isResumeOnboarding = pathname.startsWith("/resume-onboarding") || pathname.startsWith("/continue");
        if (!isResumeOnboarding && (err as Error).message.includes("Unauthorized")) {
          localStorage.removeItem("token");
          window.location.replace("/login");
        }
      },
    },
    mutations: {
      onError(err: unknown) {
        const pathname = typeof window !== "undefined" ? window.location.pathname : "";
        const isResumeOnboarding = pathname.startsWith("/resume-onboarding") || pathname.startsWith("/continue");
        if (!isResumeOnboarding && (err as Error).message.includes("Unauthorized")) {
          localStorage.removeItem("token");
          window.location.replace("/login");
        }
      },
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthContext>
        <TripsContextProvider>
          <Toaster position="top-right" expand={true} />
          <RouterProvider router={router} />
        </TripsContextProvider>
      </AuthContext>
    </QueryClientProvider>
  );
}

export default App;
